import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Review, PlatformRating } from '../type';
import { reviewsCacheService } from '../services/ReviewsCacheService';
import {
    clearReviewsCommentsClientDedupe,
    syncReviewCountsAfterServerUpdate,
} from '../services/SocialNetworkService';

interface UseOptimizedReviewsV2Return {
    reviews: Review[];
    platformRatings: PlatformRating[];
    isLoading: boolean;
    refreshReviews: () => void;
    syncCounts: () => Promise<void>;
}

export const useOptimizedReviews = (userId: string): UseOptimizedReviewsV2Return => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [platformRatings, setPlatformRatings] = useState<PlatformRating[]>([]);
    const [isLoading, setIsLoading] = useState(() => Boolean(userId));
    const location = useLocation();

    const isMyReviewsPage = location.pathname.includes('/reviews');

    const loadReviews = useCallback(async () => {
        if (!userId) {
            setIsLoading(false);
            setReviews([]);
            setPlatformRatings([]);
            return;
        }

        setIsLoading(true);

        try {
            if (isMyReviewsPage) {
                const { reviews: reviewsWithComments, platformRatings: ratings } =
                    await reviewsCacheService.getReviewsWithCommentsOptimized(userId);

                setReviews(reviewsWithComments);
                setPlatformRatings(ratings);
                setIsLoading(false);
            } else {
                const { reviews: standardReviews, platformRatings: standardRatings } =
                    await reviewsCacheService.getReviews(userId, false);

                setReviews(standardReviews);
                setPlatformRatings(standardRatings);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('[useOptimizedReviewsV2] Erreur lors du chargement:', error);
            setIsLoading(false);
        }
    }, [userId, isMyReviewsPage]);

    const refreshReviews = useCallback(() => {
        if (userId) {
            clearReviewsCommentsClientDedupe(userId);
        }
        loadReviews();
    }, [userId, loadReviews]);

    const syncCounts = useCallback(async () => {
        if (!userId) return;

        try {
            await syncReviewCountsAfterServerUpdate(userId);
            const cached = reviewsCacheService.getCacheInfo(userId);
            if (cached?.hasInitialData) {
                setReviews([...cached.reviews]);
                setPlatformRatings([...cached.platformRatings]);
            } else {
                await loadReviews();
            }
        } catch (error) {
            console.error(
                '[useOptimizedReviews] Erreur lors de la synchronisation des compteurs:',
                error,
            );
        }
    }, [userId, loadReviews]);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    return {
        reviews,
        platformRatings,
        isLoading,
        refreshReviews,
        syncCounts,
    };
};
